import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import EXTERNAL_URLS from 'constants/externalUrls';
import { get } from 'helpers/apiHelpers';
import CardBody from 'components/Card/CardBody';
import CardWrapper from 'components/Card/CardWrapper';

import useGetConsentsDataFromBrandTerm from './hooks/useGetConsentsDataFromBrandTerm';
import MarketingConsentsRow from './MarketingConsentsRow/MarketingConsentsRow';
import AdditionalInfoLabel from './AdditionalInfoLabel/AdditionalInfoLabel';

const useStyles = makeStyles({
  cardBody: {
    overflowX: 'auto',
  },
});

const CONSENT_TYPES = {
  email: 'EMAIL',
  phone: 'PHONE',
  sms: 'SMS',
};

const getConsentsDataInitialState = () => ({
  id: '',
  consent: {},
  active: false,
  additionalInfo: false,
  additionalContent: {},
});

const MarketingConsents = () => {
  const classes = useStyles();

  const { t } = useTranslation();

  const getConsentsDataFromBrandTerm = useGetConsentsDataFromBrandTerm();

  const [emailConsentsData, setEmailConsentsData] = useState(
    getConsentsDataInitialState()
  );
  const [phoneConsentsData, setPhoneConsentsData] = useState(
    getConsentsDataInitialState()
  );
  const [smsConsentsData, setSmsConsentsData] = useState(
    getConsentsDataInitialState()
  );

  const getConsentsDataFromBrandTerms = (consentType, brandTerms) =>
    getConsentsDataFromBrandTerm(
      brandTerms.find(({ type }) => type === consentType)
    );

  useEffect(() => {
    (async () => {
      try {
        const { 'hydra:member': brandTerms } = await get('/brand-terms', {
          translations: true,
        });

        setEmailConsentsData(
          getConsentsDataFromBrandTerms(CONSENT_TYPES.email, brandTerms)
        );
        setPhoneConsentsData(
          getConsentsDataFromBrandTerms(CONSENT_TYPES.phone, brandTerms)
        );
        setSmsConsentsData(
          getConsentsDataFromBrandTerms(CONSENT_TYPES.sms, brandTerms)
        );
        // eslint-disable-next-line no-empty -- FIXME
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, []);

  const rows = [
    {
      name: t('brandCfg.emailMarketingConsents'),
      consentsData: emailConsentsData,
      setConsentsData: setEmailConsentsData,
      consentLabel: t('brandCfg.emailMarketingConsentsContent'),
      additionalInfoInfoLabel: (
        <AdditionalInfoLabel
          tooltipTitle={
            <Trans
              i18nKey="brandCfg.emailMarketingConsentsPointsInfo2"
              components={{
                br: <br />,
              }}
            />
          }
          tooltipLink={EXTERNAL_URLS.points}
        >
          {t('brandCfg.emailMarketingConsentsPointsInfo')}
        </AdditionalInfoLabel>
      ),
      additionalContentLabel: t(
        'brandCfg.emailMarketingConsentsContentPointsLabel'
      ),
    },
    {
      name: t('brandCfg.smsMarketingConsents'),
      consentsData: smsConsentsData,
      setConsentsData: setSmsConsentsData,
      consentLabel: t('brandCfg.smsMarketingConsentsContent'),
      additionalInfoInfoLabel: (
        <AdditionalInfoLabel
          tooltipTitle={
            <Trans
              i18nKey="brandCfg.smsMarketingConsentsPointsInfo2"
              components={{
                br: <br />,
              }}
            />
          }
          tooltipLink={EXTERNAL_URLS.points}
        >
          {t('brandCfg.smsMarketingConsentsPointsInfo')}
        </AdditionalInfoLabel>
      ),
      additionalContentLabel: t(
        'brandCfg.smsMarketingConsentsContentPointsLabel'
      ),
    },
    {
      name: t('brandCfg.phoneMarketingConsents'),
      consentsData: phoneConsentsData,
      setConsentsData: setPhoneConsentsData,
      consentLabel: t('brandCfg.phoneMarketingConsentsContent'),
      additionalInfoInfoLabel: (
        <AdditionalInfoLabel
          tooltipTitle={
            <Trans
              i18nKey="brandCfg.phoneMarketingConsentsPointsInfo2"
              components={{
                br: <br />,
              }}
            />
          }
          tooltipLink={EXTERNAL_URLS.points}
        >
          {t('brandCfg.phoneMarketingConsentsPointsInfo')}
        </AdditionalInfoLabel>
      ),
      additionalContentLabel: t(
        'brandCfg.phoneMarketingConsentsContentPointsLabel'
      ),
    },
  ];

  return (
    <CardWrapper title={t('brandCfg.marketingConsents')}>
      <CardBody className={classes.cardBody}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('brandCfg.marketingConsentsType')}</TableCell>
              <TableCell align="center">
                {t('brandCfg.marketingConsentsActive')}
              </TableCell>
              <TableCell>{t('brandCfg.marketingConsentsContent')}</TableCell>
              <TableCell>{t('brandCfg.marketingConsentsActions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(
              ({
                name,
                consentsData,
                setConsentsData,
                consentLabel,
                additionalInfoInfoLabel,
                additionalContentLabel,
              }) => (
                <MarketingConsentsRow
                  key={consentsData.id ? JSON.stringify(consentsData) : name}
                  name={name}
                  consentsData={consentsData}
                  setConsentsData={setConsentsData}
                  consentLabel={consentLabel}
                  additionalInfoInfoLabel={additionalInfoInfoLabel}
                  additionalContentLabel={additionalContentLabel}
                />
              )
            )}
          </TableBody>
        </Table>
      </CardBody>
    </CardWrapper>
  );
};

export default MarketingConsents;
